import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/Loader';
import Paginate from 'components/Paginate';
import TableError from 'components/TableError';
import { DELETE_COMMUNITY_POST, UPDATE_COMMUNITY_POST } from 'containers/community/graphql/community.mutation';
import { COMMUNITY_REPORTED_POSTS_SEARCH } from 'containers/community/graphql/community.query';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
  Card,
  CardBody,
  Input,
} from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { ACTIVE_AND_DELETED_ARRAY, SORT_VALUES_DATE } from 'utils/constants';
import { buildURL, checkIfValueExist, printDate, printSerialNumbers, urlSlugify } from 'utils/helper';
import { SMKLinks } from 'utils/links';
import Select from 'react-select';

const ReportedPostsList = () => {
  const router = useRouter();
  const { query } = router;
  const [reportedPosts, setReportedPosts] = useState([]);
  const [reportedPostsPagination, setReportedPostsPagination] = useState({});

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    name: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [getReportedPostsRequest, { loading: reportedPostsRequestLoading }] = useLazyQuery(
    COMMUNITY_REPORTED_POSTS_SEARCH,
    {
      onCompleted: (data) => {
        const payload = data?.admin_community_post_report_search;
        if (!isEmpty(payload)) {
          setReportedPosts(payload?.results);
          setReportedPostsPagination(payload?.pagination);
        }
      },
      onError: () => {
        toast.error('Something went wrong!');
      },
    }
  );

  const [markPostActiveInActiveRequest, { loading: markActiveInActiveLoading }] = useMutation(UPDATE_COMMUNITY_POST, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_community_post_update)) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Community Post has been Updated `);
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const [markPostDeleted, { loading: markDeletedLoading }] = useMutation(DELETE_COMMUNITY_POST, {
    onCompleted: (data) => {
      if (data?.admin_community_post_remove) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Community Post has been Updated `);
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const handleChangePage = (page) => {
    getReportedPostsRequest({
      variables: {
        searchDTO: {
          page: page ? page : 1,
          size: 20,
          groupId: query?.id,
          ...filters,
          active: checkIfValueExist(filters?.active?.value),
          deleted: checkIfValueExist(filters?.deleted?.value),
          name: urlSlugify(filters?.name),
          ...SORT_VALUES_DATE,
        },
      },
    });
  };

  useEffect(() => {
    if (!isEmpty(query?.id)) {
      handleChangePage();
    }
  }, [query?.id]);

  const handlePostActive = async (data) => {
    // toggle post active
    await markPostActiveInActiveRequest({
      variables: {
        postId: data?.id,
        createDTO: {
          groupId: data?.group?.id,
          active: !data?.active,
          title: data?.title,
        },
      },
    });
  };

  const handlePostDelete = async (data) => {
    const result = await confirm({
      title: `${data?.deleted ? 'Undelete' : 'Delete'} Group`,
      message: `Are you sure, you want to ${data?.deleted ? 'Undelete' : 'Delete'} this Post?`,
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });
    if (result) {
      if (data?.deleted) {
        await markPostActiveInActiveRequest({
          variables: {
            postId: data?.id,
            createDTO: {
              groupId: data?.group?.id,
              deleted: !data?.deleted,
              active: data?.active,
              title: data?.title,
            },
          },
        });
      } else {
        await markPostDeleted({
          variables: {
            postId: data?.id,
          },
        });
      }
    }
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = async () => {
    setFilters({ ...defaultFilters, name: '' });
    await getReportedPostsRequest({
      variables: {
        searchDTO: {
          page: 1,
          size: 20,
          groupId: query?.id,
          ...SORT_VALUES_DATE,
        },
      },
    });
  };

  return (
    <>
      <Loader isActive={reportedPostsRequestLoading || markActiveInActiveLoading || markDeletedLoading} />

      <Card>
        <CardBody>
          <div className="mt-4">
            <div className="row">
              <div className="col-lg-2 mb-4">
                <label className="form-label">Active</label>
                <Select
                  options={ACTIVE_AND_DELETED_ARRAY}
                  value={filters.active}
                  onChange={(value) => setFilters({ ...filters, active: value })}
                />
              </div>
              <div className="col-lg-2 mb-4">
                <label className="form-label">Deleted</label>
                <Select
                  options={ACTIVE_AND_DELETED_ARRAY}
                  value={filters.deleted}
                  onChange={(value) => setFilters({ ...filters, deleted: value })}
                />
              </div>

              <div className="col-lg-3 mb-4">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <Input
                  type="text"
                  name="name"
                  placeholder="Name..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 pt-2">
            <a className="btn btn-primary me-4" onClick={applyFilters}>
              Apply Filters
            </a>
            <a className="" onClick={clearFilters}>
              Clear
            </a>
          </div>
        </CardBody>
      </Card>

      <Row className="mb-2 mt-4">
        <Col sm="4">
          <div className="search-box me-2 d-inline-block">
            <div className="position-relative font-bold">{reportedPostsPagination?.totalCount || 0} Posts Found</div>
          </div>
        </Col>
      </Row>

      <div className="table-responsive mt-4" style={{ minHeight: 300 }}>
        <Table className="project-list-table   table-centered table-borderless">
          <thead>
            <tr className="table-warning">
              <th scope="col">S.No.</th>
              <th scope="col">Post</th>
              <th scope="col">Comments</th>
              <th scope="col">Reason</th>
              <th scope="col">Created Date</th>
              <th scope="col">Active</th>
              <th scope="col">Deleted</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {!isEmpty(reportedPosts) ? (
              reportedPosts?.map((item, index) => {
                return (
                  <tr
                    key={`reported_post_${item.id}`}
                    className={!item?.post?.active || item?.post?.deleted ? 'bg-danger' : ''}>
                    <td>{printSerialNumbers(reportedPostsPagination)[index]}</td>
                    <td>
                      <Link
                        to={buildURL(SMKLinks.COMMUNITY_POST_DETAILS, {
                          id: item?.post?.id,
                        })}>
                        {item?.post?.title}
                      </Link>
                    </td>
                    <td>{item.comments}</td>
                    <td>{item.reason}</td>
                    <td>{printDate(item.createdDate)}</td>
                    <td>
                      <Badge className={item?.post?.active ? 'bg-success' : 'bg-danger'}>
                        {item?.post?.active ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td>
                      <Badge className={item?.post?.deleted ? 'bg-success' : 'bg-danger'}>
                        {item?.post?.deleted ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td>
                      <UncontrolledDropdown>
                        <DropdownToggle href="#" className="card-drop" tag="i">
                          <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <Link
                            to={buildURL(SMKLinks.COMMUNITY_GROUP_POSTS_EDIT, {
                              id: item?.post?.id,
                              groupId: query?.id,
                            })}>
                            <DropdownItem>
                              <i className="fas fa-pencil-alt text-success m-1 pe-3" />
                              Edit
                            </DropdownItem>
                          </Link>

                          <DropdownItem onClick={() => handlePostActive(item?.post)}>
                            <i
                              className={`fas  ${
                                item?.post?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                              }   m-1 pe-3`}
                            />
                            {item?.post?.active ? 'Disable' : 'Enable'}
                          </DropdownItem>
                          <DropdownItem onClick={() => handlePostDelete(item?.post)}>
                            <i className={`fas fa-trash-alt text-danger m-1 pe-3`} />
                            {item?.post?.deleted ? 'Undelete' : 'Delete'}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                );
              })
            ) : (
              <TableError />
            )}
          </tbody>
        </Table>

        {!isEmpty(reportedPosts) && <Paginate pageInfo={reportedPostsPagination} onChangePage={handleChangePage} />}
      </div>
    </>
  );
};

export default ReportedPostsList;
